<template>
  <v-flex pa-0>
    <SiFileUpload
      key="default"
      ref="file"
      :accept="accept"
      :save-path="savePath"
      :register-path="registerPath"
      :disabled="(!disableUpload && !savePath) || disableUploader"
      :disable-upload="disableUpload"
      :multiple="multiple"
      class="si-input-file"
      @update="updateFileList"
      @keydown.space="$refs.file.pickFile()"
    >
      <template #activator>
        <v-combobox
          ref="input"
          v-model="files"
          hide-selected
          :label="internalLabel"
          small-chips
          multiple
          filled
          single-line
          readonly
          lp-ignore
          :rules="internalRules"
          prepend-inner-icon="attachment"
          :append-icon="hasFiles ? 'close' : ''"
          @click:append="deleteAllFiles"
          @keyup.enter="pickFile"
          @keyup.space="pickFile"
        >
          <template #no-data />
          <template #selection="{ item }">
            <v-scale-transition>
              <v-chip
                :color="item.state === 'running' ? 'primary lighten-2' : 'primary'"
                label
                class="cursor-default"
                :ripple="false"
                @click.stop=""
              >
                <v-avatar left>
                  <v-progress-circular 
                    v-if="['running', 'paused', 'deleting'].includes(item.state)"
                    :indeterminate="item.progress === 100 || item.state === 'deleting'" 
                    :value="item.progress"
                    color="white" 
                    :size="20" 
                    :width="3"
                  />
                  <v-tooltip
                    v-if="item.state === 'complete'"
                    top
                  >
                    <template #activator="{ on }">
                      <v-btn
                        class="ml-2"
                        :href="item.downloadURL"
                        target="blank"
                        text
                        icon
                        small
                        color="white"
                        v-on="on"
                        @click.stop=""
                      >
                        <v-icon>insert_drive_file</v-icon>
                      </v-btn>
                    </template>
                    <span> View in new window</span>
                  </v-tooltip>
                  <!-- <v-icon v-if="item.state === 'paused'" color="white">pause</v-icon> -->
                  <v-icon
                    v-if="item.state === 'error'"
                    color="white"
                  >
                    error
                  </v-icon>
                  <v-icon
                    v-if="item.state === 'canceled'"
                    color="white"
                  >
                    remove
                  </v-icon>
                </v-avatar>

                <strong
                  v-if="['running', 'paused'].includes(item.state)"
                  class="mr-2"
                >{{ 100 > Math.floor(item.progress) ? Math.floor(item.progress) + '%' : 'FINALIZING' }}</strong>
                <span class="mr-1">{{ truncate(item.name) }}.{{ item.extension }} </span>

                <v-layout
                  v-if="item.state !== 'complete' && item.state !== 'deleting'"
                  ma-0
                >
                  <v-tooltip top>
                    <template #activator="{ on }">
                      <v-btn
                        class="ml-2"
                        :disabled="!['running','paused'].includes(item.state)"
                        text
                        icon
                        small
                        color="white"
                        v-on="on"
                        @click="pauseUpload(item)"
                      >
                        <v-icon v-if="item.state === 'running'">
                          pause
                        </v-icon>
                        <v-icon v-if="item.state === 'paused'">
                          play_arrow
                        </v-icon>
                      </v-btn>
                    </template>
                    <span
                      v-if="item.state === 'running'"
                      class="text-no-wrap"
                    >Pause upload</span>
                    <span
                      v-if="item.state === 'paused'"
                      class="text-no-wrap"
                    >Resume upload</span>
                  </v-tooltip>

                  <v-tooltip top>
                    <template #activator="{ on }">
                      <v-btn
                        class="ml-2"
                        text
                        icon
                        small
                        color="white"
                        v-on="on"
                        @click="cancelUpload(item)"
                      >
                        <v-icon>close</v-icon>
                      </v-btn>
                    </template>
                    <span class="text-no-wrap">Cancel upload</span>
                  </v-tooltip>
                </v-layout>

                <v-layout
                  v-if="item.state === 'complete'"
                  ma-0
                >
                  <v-tooltip top>
                    <template #activator="{ on }">
                      <v-btn
                        class="ml-2"
                        text
                        icon
                        small
                        color="white"
                        v-on="on"
                        @click.native.stop="deleteFile(item)"
                      >
                        <v-icon>close</v-icon>
                      </v-btn>
                    </template>
                    <span class="text-no-wrap">Remove file</span>
                  </v-tooltip>
                </v-layout>
              </v-chip>
            </v-scale-transition>
          </template>
        </v-combobox>
      </template>
    </SiFileUpload>
  </v-flex>
</template>

<script>
import { forEach } from 'lodash'
import { storage, rtdb } from '@/plugins/firebase'
import helpers from '@/mixins/helpers'
import element from '@/mixins/element'

export default {
  mixins: [ helpers, element ],
  props: {
    // label: String,
    value: [Object, Array],
    savePath: {
      type: String
    },
    registerPath: String,
    multiple: Boolean,
    rules: {
      type: Array,
      default: () => { return [] }
    },
    accept: {
      type: String,
      default: ''
    },
    disableUpload: Boolean
  },
  data () {
    return {
      errorBag: [],
      filesObj: {},
      deleting: {},
      requiredRule: () => this.hasFiles || `${this.$attrs.label || 'This field'} is required`

    }
  },
  computed: {
    additionalRules () {
      return [() => !this.hasUploading || 'Files still uploading']
    },
    errorMessages () {
      let errorBag = []
      if (this.hasUploading) {
        errorBag.push('Files still uploading')
      }
      return errorBag.concat(this.errorBag)
    },
    hasFiles () {
      return this.files.length > 0
    },
    hasErrors () {
      return (this.errorBag || []).length > 0
    },
    hasUploading () {
      let uploading = false
      forEach(this.filesObj, (file) => {
        if (file.state === 'running') uploading = true
      })
      return uploading
    },
    files () {
      let uploads = []
      forEach(this.filesObj, (file) => {
        let fileObj = Object.assign({}, file)
        delete fileObj.uploadTask
        if (!['canceled', 'deleted'].includes(file.state)) uploads.unshift(fileObj)
      })
      return uploads.reverse()
    },
    disableUploader () {
      return (this.files || []).length >= 1 && !this.multiple
    }
  },
  watch: {
    files () {
      this.emitUploadStates()
      this.validate()
      // this.$emit('input', v)
    }
  },
  mounted () {

  },
  methods: {
    truncate (str) {
      if (!str) return
      return str.length > 20 ? `${str.substr(0, 10)}...${str.substr(str.length - 4, 4)}` : str
    },
    validate () {
      if (!(this.$refs.hiddenInput || {}).hasInput) return
      let errorBag = []

      forEach(this.rules, rule => {
        errorBag.push(rule(this.hasFiles))
      })

      // errorBag.push(!this.hasUploading || 'Files still uploading')
      errorBag = errorBag.filter(x => { return x !== true })

      this.errorBag = errorBag
      return errorBag
    },
    updateFileList (file) {
      this.$set(this.filesObj, file.id, file)
    },
    statusColor (state) {
      switch (state) {
        case 'complete': return 'success'
        case 'paused': return 'grey'
        case 'running': return 'info'
        case 'error': return 'error'
        case 'deleting': return 'black'
        case 'canceled': return 'grey darken-2'
      }
    },
    cancelUpload (file) {
      if (file.state === 'paused') this.filesObj[file.id].uploadTask.resume()
      this.filesObj[file.id].uploadTask.cancel()
    },
    pauseUpload (file) {
      if (file.state === 'running') this.filesObj[file.id].uploadTask.pause()
      if (file.state === 'paused') this.filesObj[file.id].uploadTask.resume()
    },
    emitUploadStates () {
      let active; let complete; let canceled; let paused; let error = 0
      if (this.filesObj) {
        forEach(this.filesObj, file => {
          if (file.state === 'running') active++
          if (file.state === 'complete') complete++
          if (file.state === 'canceled') canceled++
          if (file.state === 'paused') paused++
          if (file.state === 'error') error++
        })
      }
      let state = {
        active: active,
        complete: complete,
        canceled: canceled,
        paused: paused,
        error: error
      }
      this.$emit('input', this.files)
      this.$emit('change', this.files)
      this.$emit('status', state)
    },
    deleteAllFiles () {
      if ((this.files || []).length === 0) return
      this.files.forEach(file => {
        this.deleteFile(file)
      })
    },
    deleteFile (file) {
      if (file.state !== 'complete') this.filesObj[file.id].uploadTask.cancel()
      file.state = 'deleting'
      this.$set(this.deleting, file.id, true)
      this.$set(this.filesObj, file.id, file)

      if (this.disableUpload) {
        this.$delete(this.filesObj, file.id)
        return
      }

      storage.ref().child(file.storagePath)
        .delete().then(() => {
          file.state = 'deleted'
          this.$delete(this.deleting, file.id)
          this.$set(this.filesObj, file.id, file)
          if (this.registerPath) {
            rtdb.ref(`${this.registerPath}/${file.id}`).set(null)
          }
        })
        .catch(error => {
          console.error(error)
          // Uh-oh, an error occurred!
        })
    },
    pickFile () {
      this.$refs.file.pickFile()
    }
  }
}
</script>

<style lang="scss">
.si-input-file.theme--light.v-text-field--filled:not(.v-input--is-focused) > .v-input__control > .v-input__slot:hover{
  background: rgba(0,0,0,0.06)
}
.si-message-field-hidden .v-input__slot{
  height: 0 !important;
  max-height: 0 !important;
  min-height: 0 !important;
}
.si-message-field-hidden .v-input__slot input{
  visibility: hidden !important;
}
.si-message-field-hidden .v-text--field__slot{
  visibility: hidden !important;
  overflow: hidden;
}

.si-input-file {
  .v-progress-circular__underlay {
    stroke: #ffffff66
  }
}

</style>
